import { RootStore } from 'store/RootStore'
import { action, observable } from 'mobx'
import { EDITOR_TIMELINE_STORE } from 'store/EditorTimelineStore'
import { EDITOR_INTERACTIVE_AREAS_STORE } from 'store/EditorInteractiveAreasStore'
import { Hotkey } from './types'
import { EDITOR_CONTEXT_MENU_STORE, actionTypes } from 'store/EditorContextMenuStore'
import { GENERAL_STORE, ModalComponentKeyTypes } from 'store/GeneralStore'
import { EDITOR_SHOTS_STORE } from 'store/EditorShotsStore'
import { EDITOR_STORE } from 'store/EditorStore'
import { EDITOR_MONITOR_STORE } from 'store/EditorMonitorStore'
import { EDITOR_ACTION_HISTORY_STORE } from 'store/EditorActionHistoryStore'

export const EDITOR_HOTKEYS_STORE = 'EDITOR_HOTKEYS_STORE'

export class EditorHotkeysStore {
  @observable searchOnFocus: boolean = false

  rootStore: RootStore;

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  showHideHotkeysList = () => {
    const {
      [GENERAL_STORE]: {
        modalDialogDetails,
        setDetailsAndShowModalDialog,
        hideModalDialog
      }
    } = this.rootStore

    modalDialogDetails.show
      ? hideModalDialog()
      : setDetailsAndShowModalDialog({
        title: 'Keyboard shortcuts',
        componentKey: ModalComponentKeyTypes.HOTKEYS_LIST
      })
  }

  togglePlayPause = () => {
    const { isPlaying, play, stop } = this.rootStore[EDITOR_TIMELINE_STORE]

    isPlaying
      ? stop()
      : play()
  }

  setSearchFieldOnFocus = () => {
    this.setSearchOnFocus(true)
  }

  goFiveFramesAhead = () => {
    this.rootStore[EDITOR_TIMELINE_STORE].incrementCurrentMonitorThumbnailIndex(5)
  }

  goFiveFramesBack = () => {
    this.rootStore[EDITOR_TIMELINE_STORE].decrementCurrentMonitorThumbnailIndex(5)
  }

  selectNextIaInMonitor = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        currentFrameInteractiveAreaIds,
        selectedInteractiveAreaIds,
        setSelectedInteractiveAreaIds
      }
    } = this.rootStore

    if (!currentFrameInteractiveAreaIds.length) return

    let nextIdIndex = null
    if (!selectedInteractiveAreaIds.length) {
      nextIdIndex = 0
    } else {
      const lastSelectedId = selectedInteractiveAreaIds[selectedInteractiveAreaIds.length - 1]
      const indexOfLastSelectedIdInTheList = currentFrameInteractiveAreaIds
        .findIndex(id => id === lastSelectedId)
      const isLastSelectedLastIndex =
        indexOfLastSelectedIdInTheList === currentFrameInteractiveAreaIds.length - 1

      nextIdIndex = isLastSelectedLastIndex ? 0 : indexOfLastSelectedIdInTheList + 1
    }
    setSelectedInteractiveAreaIds([currentFrameInteractiveAreaIds[nextIdIndex]])
  }

  selectPrevIaInMonitor = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        currentFrameInteractiveAreaIds,
        selectedInteractiveAreaIds,
        setSelectedInteractiveAreaIds
      }
    } = this.rootStore

    if (!currentFrameInteractiveAreaIds.length) return

    let prevIdIndex = null
    if (!selectedInteractiveAreaIds.length) {
      prevIdIndex = 0
    } else {
      const firstSelectedId = selectedInteractiveAreaIds[0]
      const indexOfFirstSelectedIdInTheList = currentFrameInteractiveAreaIds
        .findIndex(id => id === firstSelectedId)
      const isFirstSelectedFirstIndex = indexOfFirstSelectedIdInTheList === 0

      prevIdIndex = isFirstSelectedFirstIndex
        ? currentFrameInteractiveAreaIds.length - 1
        : indexOfFirstSelectedIdInTheList - 1
    }
    setSelectedInteractiveAreaIds([currentFrameInteractiveAreaIds[prevIdIndex]])
  }

  jumpToPreviousShotInGroup = () => {
    this.jumpToShotInGroup('previous')
  }

  jumpToNextShotInGroup = () => {
    this.jumpToShotInGroup('next')
  }

  jumpToShotInGroup = (direction: 'next' | 'previous') => {
    const {
      [EDITOR_TIMELINE_STORE]: {
        currentMonitorGroupShots,
        currentMonitorShotId,
        setCurrentMonitorFrame
      },
      [GENERAL_STORE]: { showInfo }
    } = this.rootStore

    if (!currentMonitorShotId) return

    if (!currentMonitorGroupShots.length) {
      showInfo('This shot is not grouped.')
      return
    }

    const indexOfTheCurrentShot = currentMonitorGroupShots
      .findIndex(s => s.id === currentMonitorShotId)

    const nextShotIndex = {
      next: indexOfTheCurrentShot === currentMonitorGroupShots.length - 1
        ? 0
        : indexOfTheCurrentShot + 1,
      previous: indexOfTheCurrentShot === 0
        ? currentMonitorGroupShots.length - 1
        : indexOfTheCurrentShot - 1
    }[direction]

    setCurrentMonitorFrame(currentMonitorGroupShots[nextShotIndex].startFrame)
  }

  jumpToShotEnd = () => {
    this.jumpToShotFrame('endFrame')
  }

  jumpToShotStart = () => {
    this.jumpToShotFrame('startFrame')
  }

  jumpToShotFrame = (frameType: 'startFrame' | 'endFrame') => {
    const {
      [EDITOR_TIMELINE_STORE]: {
        currentMonitorShot,
        currentMonitorFrame,
        setCurrentMonitorFrame
      }
    } = this.rootStore

    if (!currentMonitorFrame || currentMonitorShot[frameType] === currentMonitorFrame) return

    setCurrentMonitorFrame(currentMonitorShot[frameType])
  }

  @action.bound setSearchOnFocus (value: boolean = false) {
    if (this.searchOnFocus === value) return
    this.searchOnFocus = value
  }

  mergeCurrentWithNextShot = () => {
    this.mergeCurrentShot('next')
  }

  mergeCurrentWithPrevShot = () => {
    this.mergeCurrentShot('previous')
  }

  mergeCurrentShot = (direction: 'next' | 'previous') => {
    const {
      [EDITOR_TIMELINE_STORE]: { currentMonitorShotId },
      [EDITOR_SHOTS_STORE]: { sortedShotIds, mergeShots, getShotById },
      [GENERAL_STORE]: { showError }
    } = this.rootStore

    if (!currentMonitorShotId) return

    const currentShotIndex = sortedShotIds.findIndex(id => Number(id) === currentMonitorShotId)
    const otherShotIndex = {
      next: currentShotIndex + 1,
      previous: currentShotIndex - 1
    }[direction]
    const shotId2 = sortedShotIds[otherShotIndex]
    if (currentShotIndex < 0 || !shotId2) {
      showError(`${direction.toUpperCase()} shot is not available.`)
      return
    }

    const shotIds = [currentMonitorShotId, shotId2]
      .sort((a, b) => getShotById(a).startFrame - getShotById(b).startFrame)

    mergeShots(shotIds[0], shotIds[1])
  }

  hotkeys = (): Hotkey[] => [
    {
      keyCombination: 'shift+/',
      action: this.showHideHotkeysList,
      description: 'Show / Hide this help menu'
    },
    {
      keyCombination: 'space',
      action: this.togglePlayPause,
      description: 'Play / Pause'
    },
    {
      keyCombination: 'ctrl+f',
      action: this.setSearchFieldOnFocus,
      description: 'Search Library'
    },
    {
      keyCombination: 'right',
      action: this.goFiveFramesAhead,
      description: 'Jump Forward'
    },
    {
      keyCombination: 'left',
      action: this.goFiveFramesBack,
      description: 'Jump Backward'
    },
    {
      keyCombination: 'ctrl+left',
      action: this.selectPrevIaInMonitor,
      description: 'Select Previous Interactive Area in Monitor'
    },
    {
      keyCombination: 'ctrl+right',
      action: this.selectNextIaInMonitor,
      description: 'Select Next Interactive Area in Monitor'
    },
    {
      keyCombination: 'alt+left',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].jumpToPreviousShotStart,
      description: 'Jump To Previous Shot Start'
    },
    {
      keyCombination: 'alt+right',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].jumpToNextShotStart,
      description: 'Jump To Next Shot Start'
    },
    {
      keyCombination: 'alt+shift+left',
      action: this.jumpToPreviousShotInGroup,
      description: 'Jump To Previous Shot in group'
    },
    {
      keyCombination: 'alt+shift+right',
      action: this.jumpToNextShotInGroup,
      description: 'Jump To Next Shot in group'
    },
    {
      keyCombination: 'alt+up',
      action: this.jumpToShotEnd,
      description: 'Jump to shot end'
    },
    {
      keyCombination: 'alt+down',
      action: this.jumpToShotStart,
      description: 'Jump to shot start'
    },
    {
      keyCombination: 'ctrl+a',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].selectAllIAs,
      description: 'Select all Interactive areas'
    },
    {
      keyCombination: 'ctrl+c',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].copyIas,
      description: 'Copy selected Interactive areas'
    },
    {
      keyCombination: 'ctrl+v',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].pasteIas,
      description: 'Paste copied Interactive areas'
    },
    {
      keyCombination: 'delete',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].deleteSelectedIasFromCurrentShot,
      description: 'Delete selected Interactive areas from this shot'
    },
    {
      keyCombination: 'ctrl+shift+delete',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].deleteSelectedIas,
      description: 'Delete selected Interactive areas from all shot'
    },
    {
      keyCombination: 'ctrl+d',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].detachEntityFromIas,
      description: 'Detach Person/Product from selected Interactive areas'
    },
    {
      keyCombination: 'ctrl+shift+[',
      action: this.mergeCurrentWithNextShot,
      description: 'Merge current shot with next shot'
    },
    {
      keyCombination: 'ctrl+shift+]',
      action: this.mergeCurrentWithPrevShot,
      description: 'Merge current shot with previous shot'
    },
    {
      keyCombination: '\\',
      action: this.rootStore[EDITOR_TIMELINE_STORE].switchTimelineSyncingToMonitor,
      description: 'Toggle monitor sync On/Off'
    },
    {
      keyCombination: '/',
      action: this.rootStore[EDITOR_MONITOR_STORE].switchShowAiPictures,
      description: 'Product/Person IA pictures On/Off'
    },
    {
      keyCombination: 'ctrl+i',
      action: () => this.rootStore[EDITOR_CONTEXT_MENU_STORE]
        .markPointAs(actionTypes.markAsIn),
      description: 'Mark IN point for selected Interactive areas'
    },
    {
      keyCombination: 'ctrl+o',
      action: () => this.rootStore[EDITOR_CONTEXT_MENU_STORE]
        .markPointAs(actionTypes.markAsOut),
      description: 'Mark OUT point for selected Interactive areas'
    },
    {
      keyCombination: 'ctrl+j',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].copyIasToShotsInGroup,
      description: 'Copy selected Interactive areas to shots in group'
    },
    {
      keyCombination: 'f1',
      action: this.rootStore[EDITOR_STORE].openShotsWizard,
      description: 'Open Shots Wizard'
    },
    {
      keyCombination: 'f2',
      action: this.rootStore[EDITOR_STORE].openTimelineTagEditor,
      description: 'Open Timeline tag Editor'
    },
    {
      keyCombination: 'ctrl+l',
      action: this.rootStore[EDITOR_CONTEXT_MENU_STORE].handleSetLocation,
      description: 'Set current shot as Location end'
    },
    {
      keyCombination: 'ctrl+z',
      action: this.rootStore[EDITOR_ACTION_HISTORY_STORE].undo,
      description: 'Undo last action'
    },
    {
      keyCombination: 'ctrl+y',
      action: this.rootStore[EDITOR_ACTION_HISTORY_STORE].redo,
      description: 'Redo last undone action'
    }
  ]
}
